import PollCard from "@/components/Polls/PollCard";
import { authClient } from "@/lib/better-auth/client";
import { PollWithOptions } from "@/lib/server-functions/helper-functions/fetch-polls";
import { useCreateBookmark } from "@/lib/tanstack-query/hooks/useCreateBookmark";
import { useCreateReaction } from "@/lib/tanstack-query/hooks/useCreateReaction";
import { useCreateVote } from "@/lib/tanstack-query/hooks/useCreateVote";
import { QUERY_KEYS } from "@/lib/tanstack-query/queryKeys";
import { QueryClient, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { useEffect, useRef, useState } from "react";
import { useVirtualizer } from "@tanstack/react-virtual";
import { useUpdatePoll } from "@/lib/tanstack-query/hooks/useUpdatePoll";
import { Loader2 } from "lucide-react";

const updatePollsInPage = (
  page: PollWithOptions[],
  updatedPoll: PollWithOptions
) => page.map((p) => (p.id === updatedPoll.id ? updatedPoll : p));

const updateAllQueries = (
  updatedPoll: PollWithOptions,
  queryClient: QueryClient
) => {
  queryClient.setQueryData(
    [QUERY_KEYS.POLL, updatedPoll.shortId, updatedPoll.slug],
    updatedPoll
  );

  const POLL_LIST_KEYS = ["polls", "bookmarks"];

  const pollsQueries = queryClient.getQueryCache().findAll({
    predicate: (query) =>
      Array.isArray(query.queryKey) &&
      POLL_LIST_KEYS.some((key) => query.queryKey.includes(key)),
  });

  for (const query of pollsQueries) {
    const key = query.queryKey;
    const cachedData = queryClient.getQueryData(key);

    if (!cachedData || typeof cachedData !== "object") continue;

    // infinite query
    if ("pages" in cachedData && Array.isArray(cachedData.pages)) {
      queryClient.setQueryData(key, {
        ...cachedData,
        pages: cachedData.pages.map((page) =>
          updatePollsInPage(page, updatedPoll)
        ),
      });
    }
  }
};

const removePollFromAllQueries = (
  poll: PollWithOptions,
  queryClient: QueryClient
) => {
  queryClient.removeQueries({
    queryKey: [QUERY_KEYS.POLL, poll.shortId, poll.slug],
    exact: true,
  });

  const POLL_LIST_KEYS = ["polls", "bookmarks"];

  const pollsQueries = queryClient.getQueryCache().findAll({
    predicate: (query) =>
      Array.isArray(query.queryKey) &&
      POLL_LIST_KEYS.some((key) => query.queryKey.includes(key)),
  });

  for (const query of pollsQueries) {
    const key = query.queryKey;
    const cachedData = queryClient.getQueryData(key);

    if (!cachedData || typeof cachedData !== "object") continue;

    // infinite query
    if ("pages" in cachedData && Array.isArray(cachedData.pages)) {
      queryClient.setQueryData(key, {
        ...cachedData,
        pages: cachedData.pages.map((page) => removePollFromPage(page, poll)),
      });
    }
  }
};

const removePollFromPage = (page: PollWithOptions[], poll: PollWithOptions) => {
  if (!page || typeof page !== "object") return page;
  if (Array.isArray(page)) {
    return page.filter((p: PollWithOptions) => p.id !== poll.id);
  }
  return page;
};

interface PollCardWrapperProps {
  polls: PollWithOptions[];
}

const PollCardWrapper = ({ polls }: PollCardWrapperProps) => {
  const {
    data: userSession,
    isPending: isPendingSession,
    isRefetching: isRefetchingSession,
  } = authClient.useSession();
  authClient.useSession();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { mutateAsync: createReaction, isError: isReactionError } =
    useCreateReaction();
  const { mutateAsync: createVote, isError: isVoteError } = useCreateVote();
  const { mutateAsync: createBookmark, isError: isBookmarkError } =
    useCreateBookmark();
  const { mutateAsync: updatePoll, isError: isUpdatePollError } =
    useUpdatePoll();

  const handleReaction = async (
    poll: PollWithOptions,
    reaction: "downvote" | "upvote"
  ) => {
    if (!userSession?.user) {
      navigate({ to: "/sign-in" });
      return;
    }

    const userReaction = poll.userReaction;
    const reactionScore = poll.reactionScore;

    let newUserReaction = userReaction;
    let newReactionScore = reactionScore;

    if (!userReaction) {
      // no reaction before → increment or decrement
      newReactionScore =
        reaction === "upvote" ? reactionScore + 1 : reactionScore - 1;
      newUserReaction = reaction;
    } else if (userReaction === reaction) {
      // same vote
      newReactionScore =
        reaction === "upvote" ? reactionScore - 1 : reactionScore + 1;
      newUserReaction = null;
    } else {
      // changed opinion
      newReactionScore =
        reaction === "upvote" ? reactionScore + 2 : reactionScore - 2;
      newUserReaction = reaction;
    }

    const updatedPoll: PollWithOptions = {
      ...poll,
      userReaction: newUserReaction,
      reactionScore: newReactionScore,
    };

    updateAllQueries(updatedPoll, queryClient);

    await createReaction({ pollId: poll.id, type: reaction });
  };

  const handleVote = async (poll: PollWithOptions, optionId: string) => {
    if (poll.selectedOption) return;
    if (poll.selectedOption === optionId) return;
    if (!userSession?.user) {
      navigate({ to: "/sign-in" });
      return;
    }

    const gender = userSession.user.gender as
      | "male"
      | "female"
      | "other"
      | null
      | undefined;
    const countryCode = userSession.user.countryCode;

    if (!gender || !countryCode) {
      navigate({ to: "/sign-in" });
      return;
    }

    const updatedOptions = poll.options.map((option) => {
      const isPrev = option.id === poll.selectedOption;
      const isNew = option.id === optionId;

      if (isPrev || isNew) {
        let voteCount = option.voteCount;

        if (isPrev) {
          voteCount = Math.max(0, voteCount - 1);
        }

        if (isNew) {
          voteCount = voteCount + 1;
        }

        return {
          ...option,
          voteCount,
        };
      }

      return option;
    });

    const updatedPoll: PollWithOptions = {
      ...poll,
      selectedOption: optionId,
      options: updatedOptions,
    };

    updateAllQueries(updatedPoll, queryClient);

    await createVote({
      pollId: poll.id,
      optionId,
      pollAuthorId: poll.authorId,
      pollQuestion: poll.question,
    });

    queryClient.invalidateQueries({
      queryKey: [QUERY_KEYS.OPTIONS_STATS],
    });
  };

  const handleBookmark = async (poll: PollWithOptions) => {
    if (!userSession?.user) {
      navigate({ to: "/sign-in" });
      return;
    }

    const updatedPoll: PollWithOptions = {
      ...poll,
      bookmarkedAt: poll.bookmarkedAt ? null : new Date(),
    };

    updateAllQueries(updatedPoll, queryClient);

    await createBookmark({ pollId: poll.id });
  };

  const handleDelete = async (poll: PollWithOptions) => {
    if (!userSession?.user) {
      navigate({ to: "/sign-in" });
      return;
    }

    removePollFromAllQueries(poll, queryClient);

    await updatePoll({ id: poll.id, deleted: true });
  };

  const parentRef = useRef(null);

  const virtualizer = useVirtualizer({
    count: polls.length,
    getScrollElement: () => parentRef.current,
    estimateSize: () => 400,
    overscan: 5,
  });

  return (
    <>
      <div ref={parentRef}>
        <div
          style={{
            height: `${virtualizer.getTotalSize()}px`,
            width: "100%",
            position: "relative",
          }}
        >
          <div
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              width: "100%",
              transform: `translateY(${virtualizer.getVirtualItems()[0]?.start ?? 0}px)`,
            }}
            className="divide-border-wu divide-y"
          >
            {virtualizer.getVirtualItems().map((virtualItem) => {
              const poll = polls[virtualItem.index];

              return (
                <PollCard
                  data-index={virtualItem.index}
                  ref={virtualizer.measureElement}
                  key={poll.id}
                  poll={poll}
                  onReaction={(reaction) => handleReaction(poll, reaction)}
                  onVote={(optionId) => handleVote(poll, optionId)}
                  onBookmark={() => handleBookmark(poll)}
                  onDelete={() => handleDelete(poll)}
                  disabled={isPendingSession || isRefetchingSession}
                />
              );
            })}
          </div>
        </div>
      </div>
    </>
  );
};

export default PollCardWrapper;
